import { useRef } from "react";
import ShoeLoader from "./loaders/ShoeLoader";

const PaginatedTable = ({
	columns,
	data,
	filterFields,
	filters,
	onClearFilter,
	onFilterChange,
	onPageChange,
	onLimitChange,
	onRowClick,
	itemsPerPage = 10,
	currentPage = 1,
	totalCount = 0,
	rowKey = (row, idx) => idx,
	loading = false,
}) => {
	const totalPages = Math.ceil(totalCount / itemsPerPage);

	let startPage = Math.max(1, currentPage - 2);
	let endPage = Math.min(totalPages, currentPage + 2);

	if (endPage - startPage < 4) {
		if (startPage === 1) {
			endPage = Math.min(totalPages, startPage + 4);
		} else if (endPage === totalPages) {
			startPage = Math.max(1, endPage - 4);
		}
	}

	const pageNumbers = [];
	for (let i = startPage; i <= endPage; i++) {
		pageNumbers.push(i);
	}

	const debounceTimeouts = useRef({});

	const handleFilterInput = (e) => {
		const { name, value } = e.target;
		onFilterChange({ target: { name, value } });

		if (debounceTimeouts.current[name]) {
			clearTimeout(debounceTimeouts.current[name]);
		}

		debounceTimeouts.current[name] = setTimeout(() => {}, 800);
	};

	const handleLimitChange = (e) => {
		if (onLimitChange) {
			onLimitChange(Number(e.target.value));
		}
	};

	const renderClearButton = (field) => {
		const hasValue = filters[field.dataKey];

		if (!hasValue) return null;

		return (
			<button
				type="button"
				onClick={() => onClearFilter?.(field.dataKey)}
				aria-label={`${field.title || field.placeholder || field.dataKey} törlése`}
				title="Törlés"
				style={{
					position: "absolute",
					right: 10,
					top: "50%",
					transform: "translateY(-50%)",
					border: "none",
					background: "transparent",
					padding: 0,
					margin: 0,
					fontSize: 16,
					lineHeight: 1,
					color: "#9aa0a6",
					cursor: "pointer",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					zIndex: 2,
				}}
			>
				×
			</button>
		);
	};

	return (
		<div
			className="elite-table"
			style={{
				background: "#fff",
				boxShadow: "0 4px 24px rgba(92,150,50,0.10)",
				padding: 16,
				marginBottom: 24,
			}}
		>
			<div
				style={{
					marginBottom: 16,
					display: "flex",
					flexWrap: "wrap",
					gap: 8,
				}}
			>
				{filterFields.map((field, idx) => {
					const commonControlStyle = {
						maxWidth: 220,
						minWidth: 120,
						borderRadius: 0,
						fontSize: 15,
						boxShadow: "0 1px 2px rgba(92,150,50,0.04)",
						paddingRight: 30,
					};

					if (field.type === "dropdown") {
						return (
							<div
								key={field.dataKey || idx}
								style={{
									position: "relative",
									display: "inline-block",
								}}
							>
								<select
									name={field.dataKey}
									value={filters[field.dataKey] || ""}
									onChange={handleFilterInput}
									className="form-select"
									style={commonControlStyle}
								>
									<option value="">
										{field.placeholder || "Válasszon..."}
									</option>
									{field.options &&
										field.options.map((opt) => (
											<option key={opt.value} value={opt.value}>
												{opt.label}
											</option>
										))}
								</select>

								{renderClearButton(field)}
							</div>
						);
					}

					return (
						<div
							key={field.dataKey || idx}
							style={{
								position: "relative",
								display: "inline-block",
							}}
						>
							<input
								name={field.dataKey}
								value={filters[field.dataKey] || ""}
								onChange={handleFilterInput}
								placeholder={field.placeholder}
								autoComplete="off"
								className="form-control"
								style={commonControlStyle}
							/>

							{renderClearButton(field)}
						</div>
					);
				})}
			</div>

			<div
				className="elite-table-pagination"
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 16,
					marginBottom: 12,
					flexWrap: "wrap",
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
					<button
						className="btn-elite"
						disabled={currentPage === 1}
						onClick={() => onPageChange(1)}
					>
						«
					</button>

					<button
						className="btn-elite"
						disabled={currentPage === 1}
						onClick={() => onPageChange(currentPage - 1)}
					>
						&lt;
					</button>

					{pageNumbers.map((page) => (
						<button
							className="btn-elite"
							key={page}
							disabled={currentPage === page}
							onClick={() => onPageChange(page)}
							style={
								currentPage === page
									? {
											backgroundColor: "#8fe451",
											color: "#fff",
											fontWeight: 600,
									  }
									: {}
							}
						>
							{page}
						</button>
					))}

					<button
						className="btn-elite"
						disabled={currentPage === totalPages || totalPages === 0}
						onClick={() => onPageChange(currentPage + 1)}
					>
						&gt;
					</button>

					<button
						className="btn-elite"
						disabled={currentPage === totalPages || totalPages === 0}
						onClick={() => onPageChange(totalPages)}
					>
						»
					</button>
				</div>

				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 12,
						minWidth: 180,
						justifyContent: "flex-end",
					}}
				>
					{typeof totalCount === "number" && (
						<span
							style={{
								fontSize: 15,
								color: "#5c9632",
								fontWeight: 500,
							}}
						>
							{totalCount} találat
						</span>
					)}

					<select
						value={itemsPerPage}
						onChange={handleLimitChange}
						style={{
							borderRadius: 0,
							padding: "4px 10px",
							fontSize: 15,
							color: "#5c9632",
							background: "#fff",
						}}
					>
						{[10, 20, 50, 100].map((num) => (
							<option key={num} value={num}>
								{num}/ oldal
							</option>
						))}
					</select>
				</div>
			</div>

			<div style={{ width: "100%" }}>
				<div
					style={{
						maxHeight: 420,
						overflowY: "auto",
						width: "100%",
						borderRadius: 0,
					}}
				>
					<table
						className="table table-bordered table-striped align-middle"
						style={{
							borderCollapse: "collapse",
							width: "100%",
							tableLayout: "fixed",
							margin: 0,
						}}
					>
						<thead>
							<tr>
								{columns.map((col, idx) => (
									<th
										key={col.dataKey || idx}
										style={{
											textAlign: "left",
											position: "sticky",
											top: 0,
											zIndex: 2,
											background: "#5c9632",
											color: "#fff",
											fontWeight: 700,
											fontSize: 15,
											letterSpacing: "0.02em",
											padding: "12px 8px",
										}}
									>
										{col.title}
									</th>
								))}
							</tr>
						</thead>

						<tbody>
							{loading ? (
								<tr>
									<td colSpan={columns.length} style={{ textAlign: "center" }}>
										<ShoeLoader />
									</td>
								</tr>
							) : data.length === 0 ? (
								<tr>
									<td colSpan={columns.length} style={{ textAlign: "center" }}>
										Nincs találat
									</td>
								</tr>
							) : (
								data.map((row, idx) => (
									<tr
										key={rowKey(row, idx) || idx}
										onClick={onRowClick ? () => onRowClick(row) : undefined}
										style={onRowClick ? { cursor: "pointer" } : undefined}
									>
										{columns.map((col, colIdx) => (
											<td
												key={col.dataKey || colIdx}
												style={{
													whiteSpace: "nowrap",
													overflow: "hidden",
													textOverflow: "ellipsis",
													verticalAlign: "middle",
													padding: "2px 5px",
													fontSize: 15,
												}}
											>
												{row[col.dataKey]}
											</td>
										))}
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};

export default PaginatedTable;